import React from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { Skeleton } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import BadgeIcon from 'components/BadgeIcon/BadgeIcon';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import { TIER_FROM_LEVEL } from 'types/interfaces/badge';
import { UserHoverCardData } from 'types/interfaces/hoverCard';
import { HoverCardPosition } from 'hooks/useHoverCard';
import './UserHoverCard.css';

interface UserHoverCardProps {
  data?: UserHoverCardData;
  isLoading: boolean;
  position: HoverCardPosition;
  cardProps: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
}

const StarRating: React.FC<{ rating: number; count: number }> = ({ rating, count }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    if (rating >= i) {
      stars.push(
        <span key={i} className="user-hover-card__star user-hover-card__star--filled">&#9733;</span>
      );
    } else if (rating >= i - 0.5) {
      stars.push(
        <span key={i} className="user-hover-card__star user-hover-card__star--half">&#9733;</span>
      );
    } else {
      stars.push(
        <span key={i} className="user-hover-card__star">&#9733;</span>
      );
    }
  }

  return (
    <div className="user-hover-card__rating">
      <div className="user-hover-card__stars">{stars}</div>
      <span className="user-hover-card__rating-count">({count})</span>
    </div>
  );
};

const formatLocation = (city: string | null, state: string | null): string | null => {
  const parts = [city, state].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
};

const LoadingSkeleton: React.FC = () => (
  <>
    <div className="user-hover-card__skeleton-header">
      <Skeleton variant="circular" width={48} height={48} sx={{ bgcolor: 'rgba(255,255,255,0.06)' }} />
      <div style={{ flex: 1 }}>
        <Skeleton variant="text" width={120} height={20} sx={{ bgcolor: 'rgba(255,255,255,0.06)' }} />
        <Skeleton variant="text" width={80} height={16} sx={{ bgcolor: 'rgba(255,255,255,0.06)' }} />
      </div>
    </div>
    <div className="user-hover-card__skeleton-lines">
      <Skeleton variant="text" width="90%" height={14} sx={{ bgcolor: 'rgba(255,255,255,0.06)' }} />
      <Skeleton variant="text" width="70%" height={14} sx={{ bgcolor: 'rgba(255,255,255,0.06)' }} />
    </div>
    <div className="user-hover-card__skeleton-stats">
      {[...Array(2)].map((_, i) => (
        <div key={i} style={{ textAlign: 'center' }}>
          <Skeleton variant="text" width={30} height={18} sx={{ bgcolor: 'rgba(255,255,255,0.06)', margin: '0 auto' }} />
          <Skeleton variant="text" width={40} height={12} sx={{ bgcolor: 'rgba(255,255,255,0.06)', margin: '0 auto' }} />
        </div>
      ))}
    </div>
  </>
);

const UserHoverCard: React.FC<UserHoverCardProps> = ({ data, isLoading, position, cardProps }) => {
  const location = data ? formatLocation(data.city, data.state) : null;

  const content = (
    <div
      className="user-hover-card"
      style={{ left: position.left, top: position.top }}
      {...cardProps}
    >
      {isLoading || !data ? (
        <LoadingSkeleton />
      ) : (
        <>
          {/* Header: Avatar + Username + Rating */}
          <div className="user-hover-card__header">
            <AvatarWithFlag
              avatarUrl={data.avatarUrl}
              username={data.username}
              size={48}
              countryCode={data.country}
              isElite={data.isElite}
              showAnimatedBorder={data.showAnimatedBorder}
              borderColor1={data.borderColor1}
              borderColor2={data.borderColor2}
            />
            <div className="user-hover-card__header-info">
              <div className="user-hover-card__username">
                {data.username}
                <EliteBadge isElite={!!data.isElite} className="user-hover-card__elite-badge" />
              </div>
              {data.averageRating !== null && data.totalRatings > 0 && (
                <StarRating rating={data.averageRating} count={data.totalRatings} />
              )}
            </div>
          </div>

          {/* Location */}
          {location && (
            <div className="user-hover-card__location">
              <FontAwesomeIcon icon="map-pin" className="user-hover-card__location-icon" />
              {location}
            </div>
          )}

          {/* Bio */}
          {data.bio && (
            <div className="user-hover-card__bio">{data.bio}</div>
          )}

          {/* Stats */}
          <div className="user-hover-card__stats">
            <div className="user-hover-card__stat">
              <div className="user-hover-card__stat-value">{data.cardsSent}</div>
              <div className="user-hover-card__stat-label">Sent</div>
            </div>
            <div className="user-hover-card__stat">
              <div className="user-hover-card__stat-value">{data.cardsReceived}</div>
              <div className="user-hover-card__stat-label">Received</div>
            </div>
          </div>

          {/* Badges */}
          {data.displayedBadges?.length > 0 && (
            <div className="user-hover-card__badges">
              {data.displayedBadges.map((badge) => {
                const tier = TIER_FROM_LEVEL[badge.level] || 'bronze';
                return (
                  <div key={badge.slug} className="user-hover-card__badge-item">
                    <BadgeIcon
                      icon={badge.icon}
                      shape={badge.shape}
                      tier={tier}
                      size={30}
                      earned
                    />
                    <span className="user-hover-card__badge-title">{badge.title}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer */}
          <div className="user-hover-card__footer">
            <Link
              to={`/profile/u/${encodeURIComponent(data.username)}`}
              className="user-hover-card__profile-link"
            >
              View Profile &rarr;
            </Link>
          </div>
        </>
      )}
    </div>
  );

  return createPortal(content, document.body);
};

export default UserHoverCard;
